import { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import { styled, Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { HorizontalLinearStepper } from 'sharedComponents/Stepper/HorizontalStepper';

import ExpressInterestAPI from 'features/Connect/api/expressInterestAPI';
import UserSavedSchoolAPI from 'features/Connect/api/userSavedSchoolAPI';
import auth from 'utils/auth';

import { ConnectGatedActions } from 'features/Connect/utils/connectEnums';
import { ConnectProfileFlowButtonGroup } from './SharedComponents/ConnectProfileFlowButtonGroup';
import {
  getBannerContent,
  getGatedActionDisplayInfo,
  updateConnectFirstProfileSetupDateTime,
} from './utils';
import {
  ConnectProfileFlowContainer,
  ConnectProfileFlowFormContainer,
  ConnectProfileFlowPaperContainer,
  ConnectProfileFlowTitle,
  ConnectProfileFlowSelectStyles,
  ConnectProfileFlowTextField,
  ConnectProfileFlowOuterContainer,
} from './styles';
import { MultiSelectWithSearch } from 'sharedComponents/Select';
import { CONNECT_PROFILE_STATE_PREFERENCE_OPTIONS } from 'features/Connect/utils/connectConstants';
import { ConnectAnnouncementBanner } from 'features/Connect/components/ConnectAnnouncementBanner/ConnectAnnouncmentBanner';
import { GroupHeaderParams, ValidationErrors, ValidationResult } from './types';
import { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete';
import { ProfileFormData } from 'types/connectTypes';
import { ConnectProfileFormDataTestIds } from 'data-testids/ConnectDataTestIds';
import { useStateParam } from '../Context/ConnectStateCodeContextProvider';
import { PROFILE_FORM_ERROR_MESSAGES, DEFAULT_OPT_IN_FOR_TEXT_MESSAGES } from './constants';
import { Checkbox, FormControlLabel, IconButton, Tooltip, Alert } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const TOOLTIP_CONFIG = {
  PLACEMENT: 'top' as const,
  LEAVE_DELAY: 300,
} as const;

export function CreateAndUpdateProfileForm(): React.ReactElement {
  const isMounted = useRef(true);
  const user = auth.getUser();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { created, modified } = user.preference;
  const createdTime = new Date(created);
  const modifiedTime = new Date(modified);
  // we're using seconds because the created and modified timestamp milliseconds are slighly off when a user is created
  const createdTimeInSeconds = Math.floor(createdTime.getTime() / 1000);
  const modifiedInSeconds = Math.floor(modifiedTime.getTime() / 1000);
  const differenceInSeconds = Math.abs(createdTimeInSeconds - modifiedInSeconds);

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const gatedAction = searchParams.get('action');
  const gatedActionSchool = searchParams.get('school');
  const signupState = useStateParam();

  const getInitialSelectedStateOptions = (stateOptions, statePreferences) => {
    return stateOptions.filter((state) => statePreferences?.includes(state.value));
  };
  const getSelectedStateOptionsValues = (selectedOptions) => {
    if (!selectedOptions) return [];
    return selectedOptions.map((state) => state.value);
  };
  const [selectedStateOptions, setSelectedStateOptions] = useState(
    getInitialSelectedStateOptions(CONNECT_PROFILE_STATE_PREFERENCE_OPTIONS, user.preference.states)
  );

  const [userFormData, setUserFormData] = useState({
    firstName: user.first_name || '',
    lastName: user.last_name || '',
    phoneNumber: user.profile.phone_cell || '',
    statePreferences: getSelectedStateOptionsValues(selectedStateOptions) || [],
    isOptInForTextMessages: DEFAULT_OPT_IN_FOR_TEXT_MESSAGES,
  });
  const [error, setError] = useState<ValidationErrors>({
    firstName: null,
    lastName: null,
    phoneNumber: null,
    statePreferences: null,
  });
  const [formIsSubmitting, setFormIsSubmitting] = useState<boolean>(false);

  const history = useHistory();
  const { flowDisplayInformation, pageDisplayInformation } = getGatedActionDisplayInfo();

  const sortedStateOptions = [...CONNECT_PROFILE_STATE_PREFERENCE_OPTIONS]
    .map((state) => ({
      ...state,
      sublabel: state.isPreLaunch ? 'Coming Soon' : undefined,
    }))
    .sort((a, b) => {
      // First sort by active status
      if (a.isActive && !b.isActive) return -1;
      if (!a.isActive && b.isActive) return 1;

      // sort pre-launch states to the bottom
      if (a.isActive && b.isActive) {
        if (!a.isPreLaunch && b.isPreLaunch) return -1;
        if (a.isPreLaunch && !b.isPreLaunch) return 1;
      }

      // sort alphabetically within each group
      return a.label.localeCompare(b.label);
    });

  const getStateGroupLabels = (option) =>
    option.isActive
      ? 'ACTIVE STATES'
      : "INACTIVE STATES (we'll let you know via email when we launch)";

  const getStatePlaceholderText = (selectedOptionsLength: number): string => {
    if (selectedOptionsLength >= 2) {
      return 'State(s)';
    }
    if (selectedOptionsLength === 1) {
      return '';
    }
    return 'Select state(s)...';
  };

  const renderGroupHeader = (params: GroupHeaderParams): JSX.Element => {
    return (
      <div key={params.key}>
        <div style={GroupHeaderStyles(theme)}>{params.group}</div>
        {Array.isArray(params.children) ? (
          params.children.map((child, index) => (
            <div key={`group-${params.group}-option-${index}`}>{child}</div>
          ))
        ) : (
          <div>{params.children}</div>
        )}
      </div>
    );
  };

  const getFormTitle = () => {
    return differenceInSeconds <= 2 ? 'Create your profile' : 'Update your profile';
  };

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError({ ...error, [event.target.name]: null });

    setUserFormData({
      ...userFormData,
      [event.target.name]:
        event.target.type === 'checkbox' ? event.target.checked : event.target.value,
    });
  };

  const handleStatePreferenceInputChange = (selectedOptions) => {
    setError({ ...error, statePreferences: null });
    setSelectedStateOptions(selectedOptions);
    setUserFormData({
      ...userFormData,
      statePreferences: getSelectedStateOptionsValues(selectedOptions),
    });
  };

  const validateForm = async (formData: ProfileFormData): Promise<ValidationResult> => {
    const { firstName, lastName, phoneNumber, statePreferences, isOptInForTextMessages } = formData;
    const errors: ValidationErrors = {};

    if (!firstName) {
      errors.firstName = PROFILE_FORM_ERROR_MESSAGES.firstName;
    }

    if (!lastName) {
      errors.lastName = PROFILE_FORM_ERROR_MESSAGES.lastName;
    }

    if (isOptInForTextMessages && !phoneNumber) {
      errors.phoneNumber = 'Phone number is required when opting in for text messages';
    } else if (phoneNumber && phoneNumber.length < 10) {
      errors.phoneNumber = PROFILE_FORM_ERROR_MESSAGES.phoneNumber;
    }

    if (!statePreferences.length) {
      errors.statePreferences = PROFILE_FORM_ERROR_MESSAGES.statePreference;
    }

    return { isValid: Object.keys(errors).length === 0, errors };
  };

  const hasFormErrors = (error: ValidationErrors) => {
    // Exclude apiError from preventing form submission
    return !!error.firstName || !!error.lastName || !!error.phoneNumber || !!error.statePreferences;
  };

  useEffect(() => {
    const saveSchool = async () => {
      const savedSchools = await UserSavedSchoolAPI.getSchoolsUserHasSaved();
      const savedSchoolIds = savedSchools.map((s) => s.school_nces_id);
      const isSchoolSaved = savedSchoolIds.includes(gatedActionSchool);
      if (!isSchoolSaved) {
        await UserSavedSchoolAPI.saveSchool(gatedActionSchool);
      }
    };
    if (gatedAction === ConnectGatedActions.SAVE_SCHOOL && gatedActionSchool) saveSchool();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateConnectFirstProfileSetupDateTime(user, signupState);
  }, [user, signupState]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchSMSPreference = async () => {
      try {
        const response = await ExpressInterestAPI.getSMSCommunicationPreference();
        if (isMounted.current) {
          setUserFormData((prev) => ({
            ...prev,
            isOptInForTextMessages: response.subscription_status === 'subscribed',
          }));
        }
      } catch (error) {
        console.error('Error fetching SMS preferences:', error);
      }
    };

    fetchSMSPreference();

    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleSaveAndContinue = async () => {
    setFormIsSubmitting(true);
    const { isValid, errors } = await validateForm(userFormData);

    if (!isValid) {
      setError((prev) => ({ ...prev, ...errors }));
      setFormIsSubmitting(false);
      return;
    }

    const { firstName, lastName, phoneNumber, statePreferences, isOptInForTextMessages } =
      userFormData;

    const userTalentProfile = {
      userId: user.id,
      firstName,
      lastName,
      phoneCell: phoneNumber,
      preferenceStates: statePreferences,
    };

    const [updateProfileResult, updatePreferenceResult] = await Promise.all([
      ExpressInterestAPI.updateTalentProfile(userTalentProfile).catch((error) => {
        console.error('Error updating user profile:', error);
        return null;
      }),
      ExpressInterestAPI.updateCommunicationPreference({
        user_id: user.id,
        message_type: 'sms',
        subscription_status: phoneNumber && isOptInForTextMessages ? 'subscribed' : 'unsubscribed',
      }).catch((error) => {
        console.error('Error updating communication preference:', error);
        return null;
      }),
    ]);

    if (!isMounted.current) return;

    if (!updateProfileResult || !updatePreferenceResult) {
      setError((prev) => ({
        ...prev,
        apiError: 'There was an error saving your profile. Please try again.',
      }));
      setFormIsSubmitting(false);
      return;
    }

    const updatedUser = await auth.getUserAsync();
    auth.setUser(updatedUser);
    setFormIsSubmitting(false);
    history.push(pageDisplayInformation.continueToUrl);
  };

  const getPrimaryButtonLabel = (isMobile: boolean, pageDisplayInformation: any) => {
    return isMobile
      ? pageDisplayInformation?.primaryButtonLabelMobile
      : pageDisplayInformation?.primaryButtonLabel;
  };

  const SMS_CONSENT_TOOLTIP = (
    <span>
      By selecting this option and providing your mobile number, you agree to receive text messages
      from Nimble as outlined in our{' '}
      <a
        href="https://www.hirenimble.com/terms"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        terms of service
      </a>{' '}
      and{' '}
      <a
        href="https://www.hirenimble.com/privacy"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        privacy policy
      </a>
      . Message & data rates may apply. Reply STOP at any time to opt-out.
    </span>
  );

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleTooltipToggle = () => {
    if (isMobile) {
      setTooltipOpen((prev) => !prev);
    }
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
  };

  return (
    <ConnectProfileFlowOuterContainer>
      {!!getBannerContent() && <ConnectAnnouncementBanner />}
      <ConnectProfileFlowContainer>
        <HorizontalLinearStepper
          activeStep={pageDisplayInformation.step}
          steps={flowDisplayInformation.stepper.steps}
          mobileSteps={flowDisplayInformation.stepper.mobileSteps}
        />
        <ConnectProfileFlowPaperContainer elevation={4}>
          <ConnectProfileFlowFormContainer component="form">
            <ConnectProfileFlowTitle
              data-testid={ConnectProfileFormDataTestIds.HEADER_TEXT}
              variant="h1"
            >
              {pageDisplayInformation.headerText || getFormTitle()}
            </ConnectProfileFlowTitle>
            <Stack spacing={2} px={{ xs: 2, sm: 8 }} py={{ xs: 1, sm: 3 }}>
              {error.apiError && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="error">{error.apiError}</Alert>
                </Box>
              )}
              <Box>
                <QuestionLabel
                  htmlFor="first-name"
                  required={pageDisplayInformation.requiredFields.includes('firstName')}
                  aria-required
                  data-testid={ConnectProfileFormDataTestIds.FIRST_NAME_QUESTION_LABEL}
                >
                  First Name
                </QuestionLabel>
                <ConnectProfileFlowTextField
                  hiddenLabel
                  id="first-name"
                  name="firstName"
                  placeholder="Enter your first name"
                  fullWidth
                  size="medium"
                  defaultValue={userFormData.firstName}
                  onChange={handleFormChange}
                  error={!!error.firstName}
                  helperText={error.firstName}
                  data-testid={ConnectProfileFormDataTestIds.FIRST_NAME_INPUT}
                />
              </Box>
              <Box>
                <QuestionLabel
                  htmlFor="last-name"
                  required={pageDisplayInformation.requiredFields.includes('lastName')}
                  aria-required
                  data-testid={ConnectProfileFormDataTestIds.LAST_NAME_QUESTION_LABEL}
                >
                  Last Name
                </QuestionLabel>
                <ConnectProfileFlowTextField
                  hiddenLabel
                  id="last-name"
                  name="lastName"
                  placeholder="Enter your last name"
                  fullWidth
                  size="medium"
                  defaultValue={userFormData.lastName}
                  onChange={handleFormChange}
                  error={!!error.lastName}
                  helperText={error.lastName}
                  data-testid={ConnectProfileFormDataTestIds.LAST_NAME_INPUT}
                />
              </Box>
              <Box>
                <QuestionLabel
                  htmlFor="phone-number"
                  data-testid={ConnectProfileFormDataTestIds.PHONE_NUMBER_QUESTION_LABEL}
                  required={userFormData.isOptInForTextMessages}
                >
                  Phone Number
                </QuestionLabel>
                <ConnectProfileFlowTextField
                  hiddenLabel
                  id="phone-number"
                  name="phoneNumber"
                  placeholder="+1 (555) 000-0000"
                  fullWidth
                  required={userFormData.isOptInForTextMessages}
                  inputProps={{
                    minLength: 10,
                  }}
                  size="medium"
                  defaultValue={userFormData.phoneNumber}
                  onChange={handleFormChange}
                  error={!!error.phoneNumber}
                  helperText={error.phoneNumber}
                  data-testid={ConnectProfileFormDataTestIds.PHONE_NUMBER_INPUT}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={userFormData.isOptInForTextMessages}
                      onChange={handleFormChange}
                      name="isOptInForTextMessages"
                    />
                  }
                  label={
                    <span>
                      Text me when principals reach out about teaching jobs
                      <Tooltip
                        title={SMS_CONSENT_TOOLTIP}
                        placement={TOOLTIP_CONFIG.PLACEMENT}
                        arrow
                        open={tooltipOpen}
                        onClose={handleTooltipClose}
                        onOpen={handleTooltipOpen}
                        disableHoverListener={isMobile}
                        disableInteractive={false}
                        leaveDelay={TOOLTIP_CONFIG.LEAVE_DELAY}
                        componentsProps={{
                          tooltip: {
                            sx: getTooltipStyles(theme),
                          },
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={getInfoIconButtonStyles(theme)}
                          onClick={handleTooltipToggle}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </span>
                  }
                  data-testid={ConnectProfileFormDataTestIds.OPT_IN_FOR_TEXT_MESSAGES_CHECKBOX}
                  sx={getFormControlLabelStyles(theme)}
                />
              </Box>
              <Box>
                <QuestionLabel
                  htmlFor="state-preferences"
                  required
                  aria-required
                  data-testid={ConnectProfileFormDataTestIds.STATE_PREFERENCES_QUESTION_LABEL}
                >
                  In which state(s) are you looking for work?
                </QuestionLabel>
                <MultiSelectWithSearch
                  values={selectedStateOptions}
                  options={sortedStateOptions}
                  handleChange={(states) => handleStatePreferenceInputChange(states)}
                  optionGroupingFunction={getStateGroupLabels}
                  renderGroupHeader={(params: AutocompleteRenderGroupParams) =>
                    renderGroupHeader(params as GroupHeaderParams)
                  }
                  tagLimit={isMobile ? 2 : 3}
                  placeholder={getStatePlaceholderText(selectedStateOptions.length)}
                  size="medium"
                  sx={ConnectProfileFlowSelectStyles(theme)}
                  showError={!!error?.statePreferences}
                  helperText={error?.statePreferences}
                  dataTestId={ConnectProfileFormDataTestIds.STATE_PREFERENCES_DROPDOWN}
                  hasSubLabel={true}
                  subLabelKeys={['sublabel']}
                />
              </Box>
            </Stack>
          </ConnectProfileFlowFormContainer>
        </ConnectProfileFlowPaperContainer>
        <ConnectProfileFlowButtonGroup
          dataTestId={ConnectProfileFormDataTestIds.BUTTON_GROUP}
          primaryButton={{
            dataTestId: ConnectProfileFormDataTestIds.SAVE_AND_CONTINUE_BUTTON,
            primaryButtonLabel: getPrimaryButtonLabel(isMobile, pageDisplayInformation),
            primaryAction: handleSaveAndContinue,
            disabled: hasFormErrors(error),
            isLoading: formIsSubmitting,
          }}
        />
      </ConnectProfileFlowContainer>
    </ConnectProfileFlowOuterContainer>
  );
}

const QuestionLabel = styled(InputLabel)({
  textWrap: 'wrap',
});

const GroupHeaderStyles = (theme: Theme) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  borderTop: '1px solid #ddd',
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.light,
});

const getInfoIconButtonStyles = (theme: Theme) => ({
  marginLeft: theme.spacing(1),
  verticalAlign: 'middle',
});

const getTooltipStyles = (theme: Theme) => ({
  bgcolor: theme.palette.common.black,
  opacity: 0.87,
  '& a:hover': {
    color: 'inherit',
    opacity: 0.8,
  },
});

const getFormControlLabelStyles = (theme: Theme) => ({
  marginLeft: theme.spacing(1),
  '& .MuiFormControlLabel-label': {
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(1),
    },
  },
});
